/**
 * ModelPriceHistoryPanel — newest-first timeline of every change
 * to a single model price. Loads on demand from
 * GET /api/model-prices/:id/history when the parent row is
 * expanded; mountnig the panel triggers the fetch.
 *
 * Each entry renders a compact diff: the columns that changed
 * between before and after, formatted with the same money helpers
 * as the rest of the page. The first row (the create event) shows
 * the initial values rather than a diff.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ModelPriceHistory } from "@dfp/api-client";
import { Badge } from "./Badge.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface Props {
  readonly priceId: string;
  readonly currency: string;
}

export function ModelPriceHistoryPanel({ priceId, currency }: Props): JSX.Element {
  const history = useQuery({
    queryKey: ["model-price-history", priceId],
    queryFn: () => api.get<readonly ModelPriceHistory[]>(`/api/model-prices/${priceId}/history`),
  });

  return (
    <div className="px-4 py-3 bg-simuflex-mist/40 border-t border-simuflex-line space-y-2">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-simuflex-slate">
        Change history
      </h3>
      {history.isLoading ? (
        <p className="text-xs text-simuflex-slate">Loading history…</p>
      ) : history.isError ? (
        <p className="text-xs text-red-700">Failed to load history.</p>
      ) : (history.data ?? []).length === 0 ? (
        <p className="text-xs text-simuflex-slate italic">No changes recorded.</p>
      ) : (
        <ol className="space-y-2">
          {history.data?.map((row) => (
            <HistoryRow key={row.id} row={row} currency={currency} />
          ))}
        </ol>
      )}
    </div>
  );
}

function HistoryRow({
  row,
  currency,
}: {
  readonly row: ModelPriceHistory;
  readonly currency: string;
}): JSX.Element {
  const diff = useMemo(() => diffSnapshots(row.before, row.after), [row.before, row.after]);
  return (
    <li className="rounded-lg border border-simuflex-line bg-white px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <EventBadge kind={row.eventKind} />
          <span className="font-mono text-[10px] text-simuflex-slate">
            {new Date(row.changedAt).toLocaleString()}
          </span>
        </div>
        {row.changedBy ? (
          <span className="font-mono text-[10px] text-simuflex-slate">
            by {row.changedBy.slice(0, 8)}…
          </span>
        ) : null}
      </div>
      {row.changeReason ? (
        <p className="italic text-simuflex-slate mb-1">"{row.changeReason}"</p>
      ) : null}
      {row.eventKind === "create" ? (
        <p className="text-simuflex-navy">
          Created with value{" "}
          <span className="font-semibold">
            {formatNumberAsMoney(row.after["valueAmountMinor"], currency)}
          </span>
          {", "}
          provider <span className="font-mono">{String(row.after["controlProvider"])}</span>
          {", "}
          risk class <span className="font-mono">{String(row.after["modelRiskClass"])}</span>.
        </p>
      ) : diff.length === 0 ? (
        <p className="text-simuflex-slate italic">No tracked field changed.</p>
      ) : (
        <ul className="space-y-0.5">
          {diff.map((d) => (
            <li key={d.field} className="text-simuflex-navy">
              <span className="font-mono text-[10px] text-simuflex-slate">{d.field}:</span>{" "}
              <span className="line-through text-simuflex-slate">
                {renderValue(d.before, d.field, currency)}
              </span>
              <span className="mx-1">→</span>
              <span className="font-semibold">{renderValue(d.after, d.field, currency)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function EventBadge({ kind }: { readonly kind: ModelPriceHistory["eventKind"] }): JSX.Element {
  switch (kind) {
    case "create":
      return <Badge tone="brand">Created</Badge>;
    case "activate":
      return <Badge tone="success">Activated</Badge>;
    case "deactivate":
      return <Badge tone="neutral">Deactivated</Badge>;
    case "update":
    default:
      return <Badge tone="warning">Updated</Badge>;
  }
}

/* ────────────────────────────────────────────────────────────────────
 *  Diff helpers
 * ──────────────────────────────────────────────────────────────────── */

interface FieldDiff {
  readonly field: string;
  readonly before: unknown;
  readonly after: unknown;
}

/**
 * Whitelist of model-price columns to compare. Pruning the diff to
 * meaningful fields keeps the timeline readable (we don't surface
 * `updatedAt` changes, for example — every row updates it).
 */
const TRACKED_FIELDS: readonly string[] = [
  "brand",
  "model",
  "valueAmountMinor",
  "valueCurrency",
  "downPaymentMinor",
  "lendingPriceMode",
  "lendingPriceMinor",
  "modelRiskClass",
  "controlProvider",
  "capabilities",
  "active",
];

function diffSnapshots(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>>,
): readonly FieldDiff[] {
  if (!before) return [];
  const out: FieldDiff[] = [];
  for (const f of TRACKED_FIELDS) {
    const b = before[f];
    const a = after[f];
    // Defensive deep-equal for arrays/objects via JSON.stringify;
    // capabilities is an array but the rest are scalars.
    if (JSON.stringify(b) !== JSON.stringify(a)) {
      out.push({ field: f, before: b, after: a });
    }
  }
  return out;
}

const MONEY_FIELDS: ReadonlySet<string> = new Set([
  "valueAmountMinor",
  "downPaymentMinor",
  "lendingPriceMinor",
]);

function renderValue(value: unknown, field: string, currency: string): string {
  if (value === null || value === undefined) return "—";
  if (MONEY_FIELDS.has(field) && typeof value === "number") {
    return formatNumberAsMoney(value, currency);
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return value.join(", ") || "(none)";
  return String(value);
}

function formatNumberAsMoney(amount: unknown, currency: string): string {
  if (typeof amount !== "number") return String(amount);
  return formatMoney(amount, currency);
}
